
import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Attendee } from '@/types/attendee';
import { Loader2, CheckCircle, AlertCircle, Heart } from 'lucide-react';
import { format } from 'date-fns';

interface ConfirmAttendanceDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  attendee: Attendee | null;
  onConfirm: () => Promise<any>;
  isConfirming: boolean;
}

const ConfirmAttendanceDialog = ({
  isOpen,
  setIsOpen,
  attendee,
  onConfirm,
  isConfirming,
}: ConfirmAttendanceDialogProps) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), 'dd/MM/yyyy HH:mm:ss');
    } catch (error) {
      return 'Không xác định';
    }
  };

  if (!attendee) return null;

  const alreadyAttended = attendee.attended;
  const hasPaid = attendee.paid;

  // Prevent confirmation if not paid
  const canConfirm = hasPaid && !alreadyAttended;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className={`sm:max-w-md ${!hasPaid && !alreadyAttended ? 'border-pink-300 bg-gradient-to-br from-pink-50 to-white' : ''}`}>
        <DialogHeader>
          <DialogTitle className={!hasPaid && !alreadyAttended ? 'text-pink-600 flex items-center gap-2' : ''}>
            {!hasPaid && !alreadyAttended && <Heart className="h-5 w-5 text-pink-500 fill-pink-200" />}
            {alreadyAttended 
              ? 'Đã xác nhận tham dự trước đó' 
              : !hasPaid 
                ? 'Chưa thanh toán' 
                : 'Xác nhận tham dự'}
          </DialogTitle>
          <DialogDescription>
            {alreadyAttended 
              ? 'Người đăng ký này đã được xác nhận tham dự trước đó.' 
              : !hasPaid
                ? 'Người đăng ký này chưa thanh toán. Cần thanh toán trước khi check-in.'
                : 'Xác nhận người tham dự này đã có mặt tại sự kiện.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!hasPaid && !alreadyAttended && (
            <div className="bg-pink-100 p-3 rounded-lg border border-pink-200 mb-4 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-pink-500" />
                  <p className="text-pink-700 font-medium">Vui lòng thanh toán trước!</p>
                </div>
                <div className="bg-pink-200 text-pink-800 text-xs px-2 py-1 rounded-full font-medium">
                  Yêu cầu thanh toán
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <div className="font-medium">Thông tin người đăng ký:</div>
            <div className="grid grid-cols-2 gap-1 text-sm">
              <div className="text-muted-foreground">Họ tên:</div>
              <div>{attendee.name}</div>
              <div className="text-muted-foreground">Mã xác nhận:</div>
              <div>{attendee.code}</div>
              <div className="text-muted-foreground">Lớp:</div>
              <div>{attendee.class}</div>
              <div className="text-muted-foreground">Đóng góp:</div>
              <div>{attendee.contribution.toLocaleString('vi-VN')} VNĐ</div>
              <div className="text-muted-foreground">Thanh toán:</div>
              <div className={hasPaid ? "text-green-600" : "text-pink-600 font-medium flex items-center gap-1"}>
                {hasPaid ? (
                  "Đã thanh toán"
                ) : (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
                    </span>
                    Chưa thanh toán
                  </>
                )}
              </div>
              <div className="text-muted-foreground">Đăng ký lúc:</div>
              <div>{formatDate(attendee.registeredAt)}</div>
              {alreadyAttended && attendee.checkedInAt && (
                <>
                  <div className="text-muted-foreground">Đã xác nhận lúc:</div>
                  <div className="text-green-600">{formatDate(attendee.checkedInAt)}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-end">
          {alreadyAttended ? (
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
              Đóng
            </Button>
          ) : !hasPaid ? (
            <Button variant="outline" onClick={() => setIsOpen(false)} 
              className="bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100 hover:text-pink-800">
              <Heart className="mr-2 h-4 w-4 text-pink-500" />
              Đóng
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isConfirming}>
                Hủy
              </Button>
              <Button onClick={handleConfirm} disabled={isConfirming || !canConfirm}>
                {isConfirming ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Đang xác nhận...
                  </>
                ) : (
                  'Xác nhận tham dự'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmAttendanceDialog;
